import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { confirmDialog, Spinner } from "@/components/ui";
import { PageActionProps, PageTitle } from "@/components/PageTitle";
import { PageWrapper } from "@/components/PageWrapper";
import { Blog, BlogServices } from "@/services/bedrock";
import {
  blogDetailUrl,
  blogListUrl,
  URLS,
  useBlogSearch,
  useInterval,
  usePageThemes,
  usePlayerSettings,
  useSocialCoachSession,
} from "@/utils";
import { CoachSettings } from "@/interfaces";
import { toast } from "sonner";
import { BlogEditor, BlogProcessingOverlay, EditableBlog, nextEditorDraft, toEditableBlog } from "./BlogEditor";
import { BlogStatusBadge } from "./BlogStatusBadge";
import { BlogPreview, BlogPreviewHandle } from "./BlogPreview";
import { themeForPreview } from "./blogPreviewPayload";
import { cleanTags } from "./blogBody";
import { copyShareUrl } from "./copyShareUrl";

export const BlogPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const searchParams = new URLSearchParams(location.search);
  const contentPageId = Number(searchParams.get("contentPageId") || "") || undefined;
  const videoProjectId = Number(searchParams.get("videoProjectId") || "") || undefined;

  const [blog, setBlog] = useState<Blog>();
  const [draft, setDraft] = useState<EditableBlog>();
  const [previewSectionOverride, setPreviewSectionOverride] = useState<{
    sectionIndex: number;
    section: Blog.Section;
  } | null>(null);
  const [previewFieldOverride, setPreviewFieldOverride] = useState<Partial<EditableBlog> | null>(null);
  const [loading, setLoading] = useState(true);

  const { isPlayer } = useSocialCoachSession();
  const { hasSetting } = usePlayerSettings(blog?.playerId ?? undefined);
  // Blogs reuse Video Page themes, so they follow the same Player-level permission.
  const canChangeTheme = !isPlayer || !!hasSetting(CoachSettings.PLAYER_MVP_THEMES);
  const listUrl = blogListUrl(!!isPlayer);

  const {
    createBlogMutation,
    updateBlogMutation,
    publishBlogMutation,
    unpublishBlogMutation,
    regenerateBlogMutation,
    archiveBlogMutation,
  } = useBlogSearch();

  const seededFromRef = useRef<Blog>();
  const creatingRef = useRef<Promise<Blog>>();
  const previewRef = useRef<BlogPreviewHandle>(null);

  const applyBlog = useCallback((next: Blog, options?: { reseedEditor?: boolean }) => {
    const seededFrom = seededFromRef.current;
    seededFromRef.current = next;

    setBlog(next);
    setDraft(current => nextEditorDraft(next, current, seededFrom, options?.reseedEditor));
  }, []);

  const loadBlog = useCallback(
    async (blogId: number) => {
      try {
        applyBlog(await BlogServices.getBlog(blogId));
      } catch (e) {
        console.error(e);
        toast.error("Failed to load the blog");
      }
    },
    [applyBlog]
  );

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      setLoading(true);
      setBlog(undefined);
      setDraft(undefined);
      setPreviewSectionOverride(null);
      setPreviewFieldOverride(null);
      seededFromRef.current = undefined;
      try {
        if (id === "new") {
          if (!contentPageId && !videoProjectId) {
            toast.error("Choose a Video Page or Video Project to write a blog from.");
            history.replace(listUrl);
            return;
          }
          creatingRef.current ??= createBlogMutation.mutateAsync(
            contentPageId ? { contentPageId } : { videoProjectId: videoProjectId! }
          );
          const created = await creatingRef.current;
          if (cancelled) return;
          applyBlog(created);
          history.replace(blogDetailUrl(!!isPlayer, created.id));
          return;
        }

        if (!isFinite(Number(id))) {
          history.replace(listUrl);
          return;
        }

        await loadBlog(Number(id));
      } catch (e) {
        console.error(e);
        if (id !== "new") toast.error("Failed to open the blog");
        if (!cancelled) history.replace(listUrl);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    start();
    return () => {
      cancelled = true;
    };
  }, [id, contentPageId, videoProjectId]);

  const { themes } = usePageThemes({ page: 1, perPage: 100 }, draft?.themeId ?? blog?.contentPageThemeId ?? undefined);

  useInterval(
    async () => {
      if (blog) await loadBlog(blog.id);
    },
    blog?.status === "GENERATING" ? 2000 : undefined
  );

  const isDirty = useMemo(() => {
    if (!blog || !draft) return false;
    return JSON.stringify(draft) !== JSON.stringify(toEditableBlog(blog));
  }, [blog, draft]);

  const save = async () => {
    if (!blog || !draft) return;
    const updated = await updateBlogMutation.mutateAsync({
      blogId: blog.id,
      request: {
        title: draft.title,
        subtitle: draft.subtitle,
        metaDescription: draft.metaDescription,
        excerpt: draft.excerpt,
        tags: cleanTags(draft.tags),
        themeId: draft.themeId,
        body: draft.body,
      },
    });
    applyBlog(updated, { reseedEditor: true });
  };

  const publish = async () => {
    if (!blog) return;
    if (isDirty) await save();
    applyBlog(await publishBlogMutation.mutateAsync(blog.id));
  };

  const unpublish = async () => {
    if (!blog) return;
    applyBlog(await unpublishBlogMutation.mutateAsync(blog.id));
  };

  const regenerate = async () => {
    if (!blog) return;
    const confirmed = await confirmDialog({
      title: "Rewrite this blog?",
      description: "The current article will be replaced with a newly written one.",
      confirmLabel: "Rewrite",
    });
    if (!confirmed) return;
    setDraft(undefined);
    applyBlog(await regenerateBlogMutation.mutateAsync(blog.id));
  };

  const archive = async () => {
    if (!blog) return;
    const confirmed = await confirmDialog({
      title: "Delete this blog?",
      description: "This blog and its public page will no longer be available.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!confirmed) return;
    await archiveBlogMutation.mutateAsync(blog.id);
    history.push(listUrl);
  };

  const shareUrl = blog?.trackedUrl || blog?.url || "";

  const isProcessing = blog?.status === "GENERATING" || blog?.status === "FAILED";
  const isLoadingEditor = !isProcessing && (loading || !blog || !draft);
  const permitted = blog?.permittedEvents ?? [];
  const readOnly = blog?.status === "GENERATING";
  const themeOptions = useMemo(() => themes.map(theme => ({ id: theme.id, label: theme.name })), [themes]);
  const previewTheme = useMemo(
    () =>
      draft ? themeForPreview(draft, themes, blog?.effectiveContentPageTheme ?? blog?.contentPageTheme) : undefined,
    [draft, themes, blog?.effectiveContentPageTheme, blog?.contentPageTheme]
  );
  const previewDraft = useMemo(() => {
    if (!draft) return draft;
    const withFields = previewFieldOverride ? { ...draft, ...previewFieldOverride } : draft;
    if (!previewSectionOverride) return withFields;
    const sections = [...(withFields.body.sections ?? [])];
    if (previewSectionOverride.sectionIndex < 0 || previewSectionOverride.sectionIndex >= sections.length) {
      return withFields;
    }
    sections[previewSectionOverride.sectionIndex] = previewSectionOverride.section;
    return { ...withFields, body: { ...withFields.body, sections } };
  }, [draft, previewSectionOverride, previewFieldOverride]);
  const complianceWarning = blog?.processingSteps?.find(step => step.status === "COMPLETE_WITH_WARNINGS");

  const liveUrl = blog?.status === "PUBLISHED" ? blog.url : undefined;
  const openLivePage = liveUrl ? () => window.open(liveUrl, "_blank") : undefined;
  const moreActions: PageActionProps[] = [];
  if (blog?.authorBlogUrl) {
    moreActions.push({ text: "View all blogs", onClick: () => window.open(blog.authorBlogUrl!, "_blank") });
  }
  if (permitted.includes("regenerate")) {
    moreActions.push({ text: "Rewrite with AI", onClick: regenerate, disabled: regenerateBlogMutation.isPending });
  }
  if (permitted.includes("unpublish")) {
    moreActions.push({ text: "Unpublish", onClick: unpublish, disabled: unpublishBlogMutation.isPending });
  }
  moreActions.push({ text: "Delete", onClick: archive });

  const secondaryAction: PageActionProps = {
    text: isDirty ? "Save" : "Saved",
    onClick: save,
    disabled: !isDirty || updateBlogMutation.isPending,
    loading: updateBlogMutation.isPending,
  };

  const primaryAction: PageActionProps | undefined = permitted.includes("publish")
    ? {
        text: "Publish",
        onClick: publish,
        disabled: publishBlogMutation.isPending,
        loading: publishBlogMutation.isPending,
      }
    : openLivePage
    ? { text: "View live", onClick: openLivePage }
    : undefined;

  return (
    <div className="flex flex-col gap-5">
      <PageTitle
        title={blog?.title || (id === "new" ? "New blog" : "Blog")}
        hideSeparator
        breadcrumbs={[{ name: "Library", link: listUrl }]}
        primaryAction={isProcessing || isLoadingEditor ? undefined : primaryAction}
        secondaryAction={isProcessing || isLoadingEditor ? undefined : secondaryAction}
        actions={isProcessing || isLoadingEditor ? undefined : moreActions}
      />

      {isProcessing ? (
        <div className="relative min-h-100">
          <BlogProcessingOverlay blog={blog} onRetry={regenerate} retrying={regenerateBlogMutation.isPending} />
        </div>
      ) : isLoadingEditor ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-10" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <BlogStatusBadge status={blog!.status} />
            {isDirty && (
              <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                Unsaved
              </span>
            )}
            {!!blog!.readingTimeMinutes && (
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                {blog!.readingTimeMinutes} min read
              </span>
            )}
            {typeof blog!.totalLinkClicks === "number" && blog!.status === "PUBLISHED" && (
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                {blog!.totalLinkClicks} clicks
              </span>
            )}
            {complianceWarning && (
              <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                {complianceWarning.details}
              </span>
            )}
          </div>

          <div className="flex flex-col xl:flex-row gap-6">
            <div className="flex-1 min-w-0">
              <BlogEditor
                draft={draft!}
                blogId={blog?.id}
                readOnly={readOnly}
                themeOptions={themeOptions}
                canChangeTheme={canChangeTheme}
                themeSettingsUrl={isPlayer ? URLS.player.mvpSettings : URLS.coach.mvpSettings}
                onChange={patch => setDraft({ ...draft!, ...patch })}
                onSectionHover={(index, blockIndex) => previewRef.current?.highlightSection(index, blockIndex)}
                onSectionExpand={(index, blockIndex) => previewRef.current?.scrollToSection(index, blockIndex)}
                onSectionInputBlur={() => previewRef.current?.clearFocusedBlock()}
                onSectionDrag={index => {
                  if (index == null) {
                    previewRef.current?.unfollowSection();
                    return;
                  }
                  previewRef.current?.followSection(index);
                }}
                onPartHover={part => previewRef.current?.hoverPart(part)}
                onPartFocus={part => {
                  if (part == null) {
                    previewRef.current?.highlightPart(null);
                    return;
                  }
                  previewRef.current?.scrollToPart(part);
                }}
                onPreviewSection={(sectionIndex, section) => {
                  setPreviewSectionOverride(section ? { sectionIndex, section } : null);
                }}
                onPreviewField={setPreviewFieldOverride}
              />
            </div>

            <div className="w-full min-w-0 xl:w-1/2 xl:self-start xl:sticky xl:top-4 xl:z-10">
              <BlogPreview
                ref={previewRef}
                blog={blog!}
                draft={previewDraft!}
                theme={previewTheme}
                author={blog!.author ?? undefined}
                shareUrl={liveUrl ? shareUrl : undefined}
                onCopyShareUrl={liveUrl ? () => copyShareUrl(shareUrl) : undefined}
                onOpenPage={openLivePage}
                className="h-[min(70dvh,640px)] min-h-80 xl:h-[max(20rem,calc(100dvh-12rem))]"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const PlayerBlogPage: React.FC = () => (
  <PageWrapper hideSeparator>
    <BlogPage />
  </PageWrapper>
);
